// usePlanetDrag — pointer drag that spins the planet independently of
// the scroll. While dragging, the scroll-driven camera is paused (the
// camera stays at its current scene anchor). On release, the camera
// lerps back to the current scene camera over 600ms.
//
// Constraints (from design brief):
//   * No 360° free orbit during scroll — drag is a brief override.
//   * Touch support: single-finger drag.
//   * Delta is exposed as a ref; consumers add it to the planet's
//     group rotation inside useFrame. This means the actual rotation
//     value lives in the R3F scene tree, not in React state.

import { useEffect, useRef } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DragDelta {
  /** Accumulated horizontal drag in radians. */
  x: number;
  /** Per-frame horizontal velocity in radians. */
  dx: number;
  /** Accumulated vertical drag (kept for analytics / future use). */
  y: number;
}

export interface PlanetDragState {
  /** True while the user is actively dragging. */
  isDragging: React.MutableRefObject<boolean>;
  /** Accumulated drag delta in radians — read inside useFrame. */
  deltaRotation: React.MutableRefObject<number>;
  /** Force a "return" lerp toward zero delta. Call on pointer up. */
  triggerRelease: () => void;
}

export interface UsePlanetDragOptions {
  /** Pixels of drag = how many radians. Default 0.005 (~30°/100px). */
  sensitivity?: number;
  /** Duration of the post-release lerp in ms. Default 600. */
  releaseLerpMs?: number;
  /** Ref to the scroll-timeline's isDragging gate. */
  suppressScrollTimelineRef?: React.MutableRefObject<boolean>;
}

// Augment window so consumers (R3F frame loop, CSS hooks) can read the
// drag delta without subscribing to React events.
declare global {
  interface Window {
    /** {x, dx, y} — current drag delta, refreshed each rAF. */
    __planetDragDelta?: DragDelta;
    /** True iff a drag is currently active. */
    __planetIsDragging?: boolean;
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * usePlanetDrag — attach to the document (or a specific element). The
 * returned `isDragging` ref also tells the camera rig to freeze.
 *
 * Writes window.__planetDragDelta each animation frame so the R3F
 * scene graph can read it without going through React state.
 */
export function usePlanetDrag(opts: UsePlanetDragOptions = {}): PlanetDragState {
  const {
    sensitivity = 0.005,
    releaseLerpMs = 600,
    suppressScrollTimelineRef,
  } = opts;

  const isDragging = useRef(false);
  const deltaRotation = useRef(0);

  // Refs that survive across captures without re-binding.
  const lastX = useRef(0);
  const lastY = useRef(0);
  const lastDX = useRef(0);
  const activePointerId = useRef<number | null>(null);
  const releaseT0 = useRef<number | null>(null);
  const releaseFrom = useRef(0);

  // Single-finger touch guard — we lock on the first pointerId and
  // ignore any subsequent fingers until that pointer is released.
  const initialised = useRef(false);

  // We expose a method callers can invoke to start a release lerp
  // without a pointer-up event (e.g. on blur / visibility change).
  const triggerRelease = () => beginRelease();

  function beginRelease() {
    if (!isDragging.current) return;
    isDragging.current = false;
    releaseT0.current = performance.now();
    releaseFrom.current = deltaRotation.current;
    if (suppressScrollTimelineRef) suppressScrollTimelineRef.current = false;
    activePointerId.current = null;
  }

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isTouch = (e: PointerEvent) => e.pointerType === 'touch';
    const isFinePointer = (e: PointerEvent) =>
      e.pointerType === 'mouse' || e.pointerType === 'pen';

    const onPointerDown = (e: PointerEvent) => {
      // Ignore non-primary buttons on mouse-like pointers.
      if (
        isFinePointer(e) &&
        e.button !== undefined &&
        e.button !== 0
      ) {
        return;
      }
      // Single-finger touch only — ignore additional fingers.
      if (isTouch(e) && activePointerId.current !== null) return;

      activePointerId.current = e.pointerId;
      lastX.current = e.clientX;
      lastY.current = e.clientY;
      lastDX.current = 0;
      isDragging.current = true;
      releaseT0.current = null;
      if (suppressScrollTimelineRef) suppressScrollTimelineRef.current = true;
      window.__planetIsDragging = true;

      // Try to capture so the pointer keeps firing even if it briefly
      // leaves any HTML element. Best-effort — touch rarely allows it.
      try {
        (e.target as Element | null)?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDragging.current) return;
      if (activePointerId.current !== e.pointerId) return;

      const dx = e.clientX - lastX.current;
      const dy = e.clientY - lastY.current;
      lastX.current = e.clientX;
      lastY.current = e.clientY;
      lastDX.current = dx * sensitivity;

      // Horizontal only drives planet rotation. Vertical movement is
      // ignored so the page scroll keeps working while dragging.
      deltaRotation.current += dx * sensitivity;
      // Touch events: suppress native scroll once the user has clearly
      // committed to horizontal dragging.
    };

    const onPointerUp = (e: PointerEvent) => {
      if (activePointerId.current !== e.pointerId) return;
      beginRelease();
      window.__planetIsDragging = false;
    };

    const onPointerCancel = (e: PointerEvent) => {
      if (activePointerId.current !== e.pointerId) return;
      beginRelease();
      window.__planetIsDragging = false;
    };

    const onBlur = () => {
      beginRelease();
      window.__planetIsDragging = false;
    };
    const onVisibility = () => {
      if (document.hidden) {
        beginRelease();
        window.__planetIsDragging = false;
      }
    };

    // Use the document so dragging continues even when the pointer
    // briefly leaves the canvas. The canvas itself has pointer-events
    // for raycasting only; HUD overlays never block pointermove.
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerCancel);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);

    initialised.current = true;

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [sensitivity, suppressScrollTimelineRef]);

  // Release lerp — runs in its own rAF so the consumer's useFrame can
  // simply read the (already-decaying) deltaRotation ref each frame.
  // Also writes window.__planetDragDelta each animation frame.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let raf = 0;
    let prevRotation = 0;

    const tick = () => {
      if (releaseT0.current !== null) {
        const elapsed = performance.now() - releaseT0.current;
        const t = Math.min(1, elapsed / releaseLerpMs);
        // Smoothstep for the return.
        const k = t * t * (3 - 2 * t);
        // Lerp from `releaseFrom` toward 0.
        deltaRotation.current = releaseFrom.current * (1 - k);
        if (t >= 1) {
          deltaRotation.current = 0;
          releaseT0.current = null;
        }
      }

      const rot = deltaRotation.current;
      const dx = rot - prevRotation;
      prevRotation = rot;
      window.__planetDragDelta = { x: rot, dx, y: 0 };
      window.__planetIsDragging = isDragging.current;

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [releaseLerpMs]);

  return {
    isDragging,
    deltaRotation,
    triggerRelease,
  };
}
